import React from "react"
import Link from "next/link"
import Image from "next/image"
import { Phone, Mail, Users } from "lucide-react"
import { useTranslation } from "react-i18next"

const SocialIcon = ({ href, children, label, isWhatsApp = false }: { href: string, children: React.ReactNode, label: string, isWhatsApp?: boolean }) => {
  const disabled = !href
  return (
    <Link
      className={`transition ease-in-out delay-0 duration-200 p-2.5 rounded-full ${disabled ? "bg-gray-100 text-gray-300 cursor-default" : "bg-orange-50 text-orange-600 hover:bg-orange-100"}`}
      href={disabled ? "#" : isWhatsApp ? `https://wa.me/${href.replace(/\s/g, '').split(',')[0]}` : href}
      target={disabled ? "_self" : "_blank"}
      aria-label={label}
    >
      {children}
    </Link>
  )
}

const ContactSection = ({ t }: { t: any }) => (
  <div className="flex flex-col sm:flex-row md:flex-col items-start sm:items-center md:items-start gap-6 w-full lg:flex-1 xl:flex-row xl:items-end">
    <div className="flex flex-row items-center gap-3">
      <div className="border border-orange-400 p-3 rounded-lg text-orange-600">
        <Mail className="w-6 h-6" />
      </div>
      <div>
        <h3 className="font-bold text-gray-900 text-lg">{t("contactBanner.contactUs")}</h3>
        <p className="text-gray-500 text-sm">{t("contactBanner.contactUsDescription")}</p>
      </div>
    </div>

    <div className="flex flex-col sm:flex-row gap-4 sm:items-center w-full sm:w-auto">
      <a href="tel:+903926502900" className="flex items-center gap-2 text-gray-700 hover:text-orange-600 font-medium">
        <div className="border border-orange-400 p-2 rounded-lg text-orange-600">
          <Phone className="w-4 h-4" />
        </div>
        +90 (392) 650 2900
      </a>
      <a href="tel:+905488502900" className="flex items-center gap-2 text-gray-700 hover:text-orange-600 font-medium">
        <div className="border border-orange-400 p-2 rounded-lg text-orange-600">
          <Phone className="w-4 h-4" />
        </div>
        +90 (548) 850 2900
      </a>
    </div>
  </div>
)

const SocialSection = ({ socialMediaLinks, t }: { socialMediaLinks: any, t: any }) => (
  <div className="flex flex-col lg:flex-row items-start sm:items-center gap-6 w-full lg:flex-1 lg:justify-end xl:max-w-fit">
    <div className="flex flex-row items-center gap-3">
      <div className="border border-orange-400 p-3 rounded-lg text-orange-600">
        <Users className="w-6 h-6" />
      </div>
      <div>
        <h3 className="font-bold text-gray-900 text-lg">{t("contactBanner.followUs")}</h3>
        <p className="text-gray-500 text-sm">{t("contactBanner.socialMediaAccounts")}</p>
      </div>
    </div>

    <div className="flex flex-row items-center gap-3 flex-wrap">
      <SocialIcon href={socialMediaLinks.whatsapp || ""} label="WhatsApp" isWhatsApp>
        <Image src="/images_public/icons/whatsapp.png" alt="WhatsApp" width={16} height={16} className="h-6 w-6" />
      </SocialIcon>

      <SocialIcon href={socialMediaLinks.instagram || ""} label="Instagram">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
      </SocialIcon>
      <SocialIcon href={socialMediaLinks.facebook || ""} label="Facebook">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" /></svg>
      </SocialIcon>
    </div>
  </div>
)

export const ContactBanner = ({ contactInfo, socialMediaLinks }: { contactInfo: any, socialMediaLinks: any }) => {
  const { t } = useTranslation()
  return (
    <div className="w-full bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm my-6">
      <div className="flex flex-col md:flex-row justify-between gap-6 sm:gap-8 items-start md:items-center">
        <ContactSection t={t} />
        <div className="hidden md:block w-px h-16 bg-gray-100"></div>
        <SocialSection socialMediaLinks={socialMediaLinks} t={t} />
      </div>
    </div>
  )
}
